import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  Typography,
  Autocomplete,
  TextField,
  CircularProgress,
  Alert,
  Chip,
  Paper,
  InputAdornment,
  Divider
} from '@mui/material';
import {
  Search as SearchIcon,
  SwapHoriz as TransferIcon
} from '@mui/icons-material';
import * as clinicalSupervisorApi from '../../../services/clinicalSupervisorApi';

function TransferClientsModal({ open, onClose, selectedClients, serviceGroups, token, onSuccess }) {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [therapists, setTherapists] = useState([]);
  const [fromTherapist, setFromTherapist] = useState(null);
  const [toTherapist, setToTherapist] = useState(null);
  const [therapistSearchLoading, setTherapistSearchLoading] = useState(false);

  useEffect(() => {
    if (open) {
      loadTherapists();
      setError('');
      setFromTherapist(null);
      setToTherapist(null);

      // Auto-detect source therapist if all selected clients have the same therapist
      if (selectedClients.length > 0) {
        const firstTherapist = selectedClients[0].assignedTherapist;
        const allSameTherapist = selectedClients.every(
          c => c.assignedTherapist?._id === firstTherapist?._id
        );

        if (allSameTherapist && firstTherapist) {
          setFromTherapist(firstTherapist);
        }
      }
    }
  }, [open, selectedClients, token]);

  const loadTherapists = async () => {
    try {
      setTherapistSearchLoading(true);
      const response = await clinicalSupervisorApi.getAssignedTherapists(true, token);
      setTherapists(response.therapists || []);
    } catch (err) {
      console.error('Error loading therapists:', err);
      setError('Failed to load therapists');
    } finally {
      setTherapistSearchLoading(false);
    }
  };

  const handleTransfer = async () => {
    if (!fromTherapist) {
      setError('Please select source therapist');
      return;
    }

    if (!toTherapist) {
      setError('Please select destination therapist');
      return;
    }

    if (fromTherapist._id === toTherapist._id) {
      setError('Source and destination therapists must be different');
      return;
    }

    if (selectedClients.length === 0) {
      setError('No clients selected');
      return;
    }

    // Verify all clients belong to source therapist
    const invalidClients = selectedClients.filter(
      c => c.assignedTherapist?._id !== fromTherapist._id
    );

    if (invalidClients.length > 0) {
      setError(`${invalidClients.length} client(s) are not assigned to ${fromTherapist.name}`);
      return;
    }

    // Check destination capacity
    const currentCount = toTherapist.therapistInfo?.assignedClientCount || 0;
    const maxCapacity = toTherapist.therapistInfo?.maxClientCapacity || 20;
    const availableSlots = maxCapacity - currentCount;

    if (selectedClients.length > availableSlots) {
      setError(`Destination therapist only has ${availableSlots} available slots. You selected ${selectedClients.length} clients.`);
      return;
    }

    try {
      setLoading(true);
      setError('');

      await clinicalSupervisorApi.transferClients(
        selectedClients.map(c => c._id),
        fromTherapist._id,
        toTherapist._id,
        token
      );

      onSuccess(`Successfully transferred ${selectedClients.length} client(s) from ${fromTherapist.name} to ${toTherapist.name}`);
      onClose();
    } catch (err) {
      console.error('Error transferring clients:', err);
      setError(err.response?.data?.msg || 'Failed to transfer clients');
    } finally {
      setLoading(false);
    }
  };

  const getClientServiceGroup = (client) => {
    const activeGroup = client.serviceGroups?.find(sg => sg.isActive && sg.roles?.includes('client'));
    return activeGroup?.groupId?.name || 'Unknown';
  };

  // Filter out the source therapist from destination options
  const availableToTherapists = toTherapist || !fromTherapist
    ? therapists
    : therapists.filter(t => t._id !== fromTherapist._id);

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle>
        <Box display="flex" alignItems="center" gap={1}>
          <TransferIcon />
          Transfer Clients Between Therapists
        </Box>
      </DialogTitle>

      <DialogContent>
        {error && (
          <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>
            {error}
          </Alert>
        )}

        {/* Selected Clients Summary */}
        <Paper sx={{ p: 2, mb: 3, backgroundColor: '#f5f5f5' }}>
          <Typography variant="subtitle2" gutterBottom>
            Selected Clients ({selectedClients.length}):
          </Typography>
          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mt: 1 }}>
            {selectedClients.slice(0, 5).map(client => (
              <Chip
                key={client._id}
                label={`${client.name} (${client.assignedTherapist?.name || 'Unassigned'})`}
                size="small"
                color="primary"
                variant="outlined"
              />
            ))}
            {selectedClients.length > 5 && (
              <Chip
                label={`+${selectedClients.length - 5} more`}
                size="small"
                color="primary"
              />
            )}
          </Box>
        </Paper>

        {/* Source Therapist Selection */}
        <Typography variant="subtitle2" gutterBottom>
          Transfer From:
        </Typography>
        <Autocomplete
          value={fromTherapist}
          onChange={(e, newValue) => {
            setFromTherapist(newValue);
            // Reset destination if same as source
            if (toTherapist && newValue && toTherapist._id === newValue._id) {
              setToTherapist(null);
            }
          }}
          options={therapists}
          getOptionLabel={(option) =>
            `${option.name} (${option.therapistInfo?.assignedClientCount || 0} clients)`
          }
          loading={therapistSearchLoading}
          renderInput={(params) => (
            <TextField
              {...params}
              placeholder="Select source therapist..."
              InputProps={{
                ...params.InputProps,
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon />
                  </InputAdornment>
                ),
                endAdornment: (
                  <>
                    {therapistSearchLoading ? <CircularProgress size={20} /> : null}
                    {params.InputProps.endAdornment}
                  </>
                ),
              }}
            />
          )}
          renderOption={(props, option) => (
            <li {...props} key={option._id}>
              <Box sx={{ width: '100%' }}>
                <Typography variant="body1">{option.name}</Typography>
                <Typography variant="caption" color="text.secondary">
                  {option.email} • {option.therapistInfo?.assignedClientCount || 0} clients
                </Typography>
              </Box>
            </li>
          )}
          sx={{ mb: 3 }}
        />

        <Divider sx={{ my: 2 }} />

        {/* Destination Therapist Selection */}
        <Typography variant="subtitle2" gutterBottom>
          Transfer To:
        </Typography>
        <Autocomplete
          value={toTherapist}
          onChange={(e, newValue) => setToTherapist(newValue)}
          options={availableToTherapists}
          getOptionLabel={(option) =>
            `${option.name} - ${option.therapistInfo?.assignedClientCount || 0}/${option.therapistInfo?.maxClientCapacity || 20} clients`
          }
          loading={therapistSearchLoading}
          disabled={!fromTherapist}
          renderInput={(params) => (
            <TextField
              {...params}
              placeholder={fromTherapist ? "Select destination therapist..." : "Select source therapist first"}
              disabled={!fromTherapist}
              InputProps={{
                ...params.InputProps,
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon />
                  </InputAdornment>
                ),
                endAdornment: (
                  <>
                    {therapistSearchLoading ? <CircularProgress size={20} /> : null}
                    {params.InputProps.endAdornment}
                  </>
                ),
              }}
            />
          )}
          renderOption={(props, option) => (
            <li {...props} key={option._id}>
              <Box sx={{ width: '100%' }}>
                <Typography variant="body1">{option.name}</Typography>
                <Typography variant="caption" color="text.secondary">
                  {option.email} • {option.therapistInfo?.assignedClientCount || 0}/{option.therapistInfo?.maxClientCapacity || 20} clients •{' '}
                  {(option.therapistInfo?.maxClientCapacity || 20) - (option.therapistInfo?.assignedClientCount || 0)} slots available
                </Typography>
              </Box>
            </li>
          )}
        />

        {/* Transfer Summary */}
        {fromTherapist && toTherapist && (
          <Paper sx={{ p: 2, mt: 2, backgroundColor: '#e3f2fd' }}>
            <Typography variant="subtitle2" gutterBottom>
              Transfer Summary:
            </Typography>
            <Box sx={{ display: 'grid', gridTemplateColumns: '1fr auto 1fr', gap: 2, alignItems: 'center', mt: 1 }}>
              <Box>
                <Typography variant="body2" fontWeight="bold">From: {fromTherapist.name}</Typography>
                <Typography variant="caption" color="text.secondary">
                  Current: {fromTherapist.therapistInfo?.assignedClientCount || 0} clients
                </Typography>
                <br />
                <Typography variant="caption" color="warning.main">
                  After: {(fromTherapist.therapistInfo?.assignedClientCount || 0) - selectedClients.length} clients
                </Typography>
              </Box>
              <TransferIcon color="primary" />
              <Box>
                <Typography variant="body2" fontWeight="bold">To: {toTherapist.name}</Typography>
                <Typography variant="caption" color="text.secondary">
                  Current: {toTherapist.therapistInfo?.assignedClientCount || 0}/{toTherapist.therapistInfo?.maxClientCapacity || 20}
                </Typography>
                <br />
                <Typography variant="caption" color={
                  ((toTherapist.therapistInfo?.maxClientCapacity || 20) - (toTherapist.therapistInfo?.assignedClientCount || 0)) >= selectedClients.length
                    ? 'success.main'
                    : 'error.main'
                }>
                  After: {(toTherapist.therapistInfo?.assignedClientCount || 0) + selectedClients.length}/{toTherapist.therapistInfo?.maxClientCapacity || 20}
                </Typography>
              </Box>
            </Box>
          </Paper>
        )}
      </DialogContent>

      <DialogActions>
        <Button onClick={onClose} disabled={loading}>
          Cancel
        </Button>
        <Button
          onClick={handleTransfer}
          variant="contained"
          disabled={loading || !fromTherapist || !toTherapist}
          startIcon={loading ? <CircularProgress size={20} /> : <TransferIcon />}
        >
          {loading ? 'Transferring...' : `Transfer ${selectedClients.length} Client${selectedClients.length !== 1 ? 's' : ''}`}
        </Button>
      </DialogActions>
    </Dialog>
  );
}

export default TransferClientsModal;
